import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DeviceServiceV2 } from '@devices';
import { type NormalizedDeviceData } from '@devices';
import { type FloorSpaceModel, LocationActions, LocationService, locationUtils, type RoomSpaceModel, SpaceType } from '@locations';
import { Store } from '@ngrx/store';
import { MessageComponent, NotificationsService } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { forkJoin } from 'rxjs';

type ConfirmDeleteLocationDialogProps = {
  locationId: string;
  name: string;
  navigateToParent: boolean;
};

@Component({
  imports: [MatDialogModule, NgxSkeletonLoaderModule, MessageComponent],
  selector: 'avs-fe-confirm-delete-location-dialog',
  templateUrl: './confirm-delete-location-dialog.component.html',
})
export class ConfirmDeleteLocationDialogComponent {
  protected dialogRef = inject(MatDialogRef<ConfirmDeleteLocationDialogComponent>);
  protected devices = signal<NormalizedDeviceData[]>([]);
  protected isLoading = signal(false);
  protected floors = signal<FloorSpaceModel[]>([]);
  protected rooms = signal<RoomSpaceModel[]>([]);
  protected data = inject<ConfirmDeleteLocationDialogProps>(MAT_DIALOG_DATA);
  private deviceServiceV2 = inject(DeviceServiceV2);
  private locationService = inject(LocationService);
  private store = inject(Store);
  private notificationService = inject(NotificationsService);

  constructor() {
    this.loadLocationDetails(this.data.locationId);
  }

  protected confirm() {
    this.store.dispatch(LocationActions.deleteLocation({
      locationId: this.data.locationId,
      navigateToParent: this.data.navigateToParent,
    }));
    this.dialogRef.close(true);
  }

  private loadLocationDetails(locationId: string) {
    this.isLoading.set(true);

    forkJoin({
      devices: this.deviceServiceV2.list({ filters: { location: locationId } }),
      floors: this.locationService.getSpacesList(locationId, {
        includeArchived: 'true',
        includeNestedDevices: 'true',
        type: SpaceType.FLOOR,
      }),
      rooms: this.locationService.getSpacesList(locationId, { includeArchived: 'true', type: SpaceType.ROOM }),
    }).subscribe({
      complete: () => {
        this.isLoading.set(false);
      },
      error: () => {
        this.notificationService.showErrorMessage('Failed to load location details');
      },
      next: ({ floors, rooms, devices }) => {
        this.floors.set(floors.filter(locationUtils.isFloor));
        this.rooms.set(rooms.filter(locationUtils.isRoom));
        this.devices.set(devices.data);
      },
    });
  }
}

export function openDeleteLocationDialog(dialog: MatDialog, data: ConfirmDeleteLocationDialogProps) {
  const config = new MatDialogConfig();

  config.disableClose = true;
  config.data = {
    ...data,
  };
  config.autoFocus = false;
  config.backdropClass = 'backdrop-modal-panel';
  const dialogRef = dialog.open(ConfirmDeleteLocationDialogComponent, config);

  return dialogRef.afterClosed();
}
